import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const LANGUAGE_NAME_MAX_LENGTH = 100;

export type LanguageListRow = {
  id: string;
  name: string;
  code: string;
  active: boolean;
};

export type LanguageDetail = {
  id: string;
  name: string;
  code: string;
  active: boolean;
};

export type SaveLanguageInput = {
  name: string;
  active: boolean;
};

type ApiLanguageRow = Record<string, unknown>;

function mapLanguageRow(row: ApiLanguageRow): LanguageListRow | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    active: bool(row.active) ?? true,
  };
}

function mapLanguageDetail(row: ApiLanguageRow): LanguageDetail | null {
  return mapLanguageRow(row);
}

function unwrapRows(data: unknown): ApiLanguageRow[] {
  if (Array.isArray(data)) return data as ApiLanguageRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.content)) return record.content as ApiLanguageRow[];
  if (Array.isArray(record.data)) return record.data as ApiLanguageRow[];
  return [];
}

/** GET /v1/masters/languages */
export async function listLanguages(): Promise<ApiResult<LanguageListRow[]>> {
  const res = await caseApiRequest<unknown>('/v1/masters/languages', { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  const rows = unwrapRows(res.data)
    .map(mapLanguageRow)
    .filter((row): row is LanguageListRow => row != null);
  return { success: true, data: rows };
}

/** GET /v1/masters/languages/{id} */
export async function getLanguageById(id: string): Promise<ApiResult<LanguageDetail>> {
  const languageId = id.trim();
  if (!languageId) return { success: false, message: 'Language id is missing.' };
  const res = await caseApiRequest<unknown>(`/v1/masters/languages/${encodeURIComponent(languageId)}`, {
    method: 'GET',
  });
  if (!res.ok) return { success: false, message: res.message };
  const detail = mapLanguageDetail(
    res.data && typeof res.data === 'object' ? (res.data as ApiLanguageRow) : {},
  );
  if (!detail) return { success: false, message: 'Language details are unavailable.' };
  return { success: true, data: detail };
}

/** POST /v1/masters/languages */
export async function createLanguage(input: SaveLanguageInput): Promise<ApiResult<void>> {
  const res = await caseApiRequest<unknown>('/v1/masters/languages', {
    method: 'POST',
    body: {
      name: input.name.trim(),
      active: input.active,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** PUT /v1/masters/languages/{id} */
export async function updateLanguage(id: string, input: SaveLanguageInput): Promise<ApiResult<void>> {
  const languageId = id.trim();
  if (!languageId) return { success: false, message: 'Language id is missing.' };
  const res = await caseApiRequest<unknown>(`/v1/masters/languages/${encodeURIComponent(languageId)}`, {
    method: 'PUT',
    body: {
      name: input.name.trim(),
      active: input.active,
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}
